/**
 * ClassicFeedTemplate - Social media style feed template
 *
 * Displays photo with critique comments below in vertical list.
 * Creates a familiar social feed experience with unified scrolling.
 *
 * Feature: 649-critique-feedback-options - Added rating display
 *
 * @module PhotoItem2/templates/ClassicFeedTemplate
 */

import { cn } from '@/lib/utils';
import type { PhotoItem2TemplateProps } from '../PhotoItem2.types';
import { CritiqueCard } from './CritiqueCard';
import { AdaptivePhoto } from '../../photos/AdaptivePhoto';
import { CompactRatingDisplay } from '../../composed/CompactRatingDisplay';
import { SelectionIndicator } from '@/components/atoms';

/**
 * ClassicFeedTemplate component
 *
 * Renders photo with critique comments displayed below in vertical feed layout.
 * Comments are sorted newest first and displayed with compact CritiqueCard components.
 * Uses AdaptivePhoto for automatic resolution selection based on container size.
 */
export function ClassicFeedTemplate({
  data,
  isSelected = false,
  selectable = false,
  zoom = 1.0, // NOTE: zoom prop no longer used - AdaptivePhoto uses container size
  onClick,
  onCritiqueClick,
  className,
  ...restProps
}: PhotoItem2TemplateProps) {
  void zoom; // Suppress unused variable warning - AdaptivePhoto uses container size instead

  // Sort critiques newest first
  const sortedCritiques = data.critiques
    ? [...data.critiques].sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      })
    : [];

  // Feature 081: photo-versioning - Forward additional props (e.g., onContextMenu) to article
  return (
    <article
      className={cn(
        'w-full p-2 flex flex-col bg-surface-1 rounded-lg overflow-hidden',
        className
      )}
      {...restProps}
    >
      {/* Image section */}
      <div
        onClick={onClick}
        className={cn(
          'relative w-full bg-surface-2',
          selectable && 'cursor-pointer hover:ring-2 hover:ring-primary/50',
          isSelected && 'ring-4 ring-primary'
        )}
      >
        <AdaptivePhoto
          photo={data.photoData}
          alt={data.photoData.description || ''}
          className="w-full h-auto"
          preferStorageUrl={true}
        />

        {/* Selection indicator */}
        <SelectionIndicator visible={isSelected} size="md" />
      </div>

      {/* Rating display - shows average for photographers, own rating for critics */}
      <div className="w-full bg-surface-1 px-4 py-2 border-b border-border">
        <CompactRatingDisplay
          photoId={data.photoData.id}
          size={16}
          variant="inline"
        />
      </div>

      {/* Description text */}
      {data.textContent && (
        <div className="w-full bg-surface-1 px-4 py-3">
          <p className="text-base leading-relaxed text-foreground">{data.textContent}</p>
        </div>
      )}

      {/* Critique comments section */}
      {sortedCritiques.length > 0 && (
        <div className="w-full bg-surface-1 p-4 space-y-2">
          {sortedCritiques.map((critique) => (
            <CritiqueCard
              key={critique.id}
              critique={critique}
              photo={data.photoData}
              expanded={false}
              onCritiqueClick={onCritiqueClick}
            />
          ))}
        </div>
      )}
    </article>
  );
}
